import React, { useRef, useEffect, useState, useCallback } from 'react';
import scrollTo from '../../../utils/scrollTo';
import './Hero.css';
import demoVideo from './demo_final_edited.mp4';
import merubotLogo from '../../../assets/images/merubot_logo.png';
import RobotSpline from './RobotSpline';
import FreeTrialModal from './FreeTrialModal';
import { useNavigate } from 'react-router-dom';

const STATS = [
  { num: '10x', label: 'Faster Screening', target: 10, suffix: 'x', isInt: true },
  { num: '95%', label: 'Evaluation Accuracy', target: 95, suffix: '%', isInt: true },
  { num: '50+', label: 'Industries Supported', target: 50, suffix: '+', isInt: true },
  { num: '500+', label: 'Job Roles Covered', target: 500, suffix: '+', isInt: true },
];

// Easing — ease out cubic
function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}


function useCountUp(target, duration = 1800, shouldStart = false) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);


  useEffect(() => {
    if (!shouldStart) return;
    const startTime = performance.now();

    function tick(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      setValue(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration, shouldStart]);

  return value;
}

function AnimatedStat({ stat, shouldStart }) {
  const count = useCountUp(stat.target, 1800, shouldStart);
  return (
    <div className="stat">
      <div className="stat-num">
        {shouldStart ? `${count}${stat.suffix}` : stat.num}
      </div>
      <div className="stat-label">{stat.label}</div>
    </div>
  );
}

export default function Hero() {
  const navigate = useNavigate()
  const demoVideoRef = useRef(null);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [showTrial, setShowTrial] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://b.sf-syn.com/badge_js?sf_id=4099055&variant_id=sf";
    script.async = true;
 
    document.body.appendChild(script);
 
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Video intersection — play/pause
  useEffect(() => {
    const video = demoVideoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.currentTime = 0;
          video.play().catch(() => { });
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  // Stats intersection — trigger count-up once
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="hero" className="hero">
      <div className="blob blob-1" />
      <div className="blob blob-2" />
      <div className="blob blob-3" />

      <div className="hero-inner">

        {/* ── Left ── */}
        <div className="hero-left">
          <div className="hero-logo-badge" onClick={() => scrollTo('hero')}>
            <img src={merubotLogo} alt="merubot" className="hero-logo-img" />
          </div>

          <h1 className="hero-h1">
            Hire <em>Smarter</em> with <span className="hero-gradient">AI-Driven Interviews</span>
          </h1>

          <p className="hero-sub">
            MeruBot automates question generation, evaluates responses with advanced AI
            and manages results so your team focuses on choosing the best talent, not running interviews.
          </p>

          <div className="hero-actions">
            <button className="btn-primary btn-green" onClick={() => scrollTo('demo')}>
              Request Demo
            </button>
            <button className="btn-ghost" onClick={() => navigate('/tenantRegister')}>
              Free Trial
            </button>
          </div>
          

          <div className="hero-stats" ref={statsRef}>
            {STATS.map((s, i) => (
              <React.Fragment key={s.label}>
                <AnimatedStat stat={s} shouldStart={statsVisible} />
                {i < STATS.length - 1 && <div className="stat-divider" />}
              </React.Fragment>
            ))}
          </div>

<div style={{ display: 'flex', alignItems: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
 
  <div
    className="sf-root"
    data-id="4099055"
    data-badge="customers-love-us-white"
    data-variant-id="sf"
    style={{ width: "125px", transform: "scale(0.75)", transformOrigin: "left center", }}
  >
    <a
      href="https://sourceforge.net/software/product/MeruBot/"
      target="_blank"
      rel="noopener noreferrer"
    >
      MeruBot Reviews
    </a>
  </div>
 
  <a
    href="https://www.producthunt.com/products/merubot?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-merubot%22"
    target="_blank"
    rel="noopener noreferrer"
    // style={{ marginTop: '20px', display: 'inline-block' }}
  >
    <img
      alt="MeruBot - Your AI Interviewer | Product Hunt"
      width="180"
      height="46"
      src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1127157&theme=light&t=1776879949875%22"
    />
  </a>    
 
 
</div>
</div>

        {/* ── Right — 3D Robot ── */}
        <div className="hero-right">
          <RobotSpline />
        </div>

      </div>

      {/* ── Free Trial Modal ── */}
      {showTrial && <FreeTrialModal onClose={() => setShowTrial(false)} />}

      {/* ── Demo Video ── */}
      {/* <div id="demo-video" className="video-wrap">
        <div className="video-bar"> */}
          {/* <span className="dot r" /><span className="dot y" /><span className="dot g" /> */}
          {/* <span className="video-url">app.merubot.ai / demo</span> */}
        {/* </div> */}
        {/* <div className="video-body">
          <video
            ref={demoVideoRef}
            src={demoVideo}
            muted
            loop
            playsInline
            preload="auto"
            className="demo-video"
          />
        </div> */}
      {/* </div> */}
    </section>
  );
}